import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, CircleDashed, AlertCircle } from "lucide-react";

function relativeAgo(ms) {
  if (ms < 5000) return "just now";
  if (ms < 60000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3600000) return `${Math.floor(ms / 60000)}m ago`;
  return `${Math.floor(ms / 3600000)}h ago`;
}

/**
 * Live auto-save status indicator for the PFS header. Re-renders the
 * "Saved Xs ago" label every 5 seconds so the timestamp stays fresh
 * without forcing the parent to tick.
 */
export default function PfsSaveStatusChip({ saving, dirty, lastSavedAt, lastError }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (saving || dirty || !lastSavedAt) return;
    const id = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(id);
  }, [saving, dirty, lastSavedAt]);

  let icon, label, tone;
  if (saving) {
    icon = <Loader2 className="w-3.5 h-3.5 animate-spin" />;
    label = "Saving…";
    tone = "text-slate-500";
  } else if (lastError) {
    icon = <AlertCircle className="w-3.5 h-3.5" />;
    label = "Save failed — will retry";
    tone = "text-rose-700";
  } else if (dirty) {
    icon = <CircleDashed className="w-3.5 h-3.5" />;
    label = "Unsaved changes";
    tone = "text-amber-700";
  } else if (lastSavedAt) {
    icon = <CheckCircle2 className="w-3.5 h-3.5" />;
    label = `Saved ${relativeAgo(now - lastSavedAt)}`;
    tone = "text-emerald-700";
  } else {
    return null;
  }

  return (
    <div
      className={`inline-flex items-center gap-1.5 text-xs font-mono ${tone}`}
      data-testid="pfs-save-status"
      data-status={saving ? "saving" : lastError ? "error" : dirty ? "dirty" : "saved"}
      title={lastSavedAt ? `Last saved ${new Date(lastSavedAt).toLocaleString()}` : undefined}
    >
      {icon}
      <span>{label}</span>
    </div>
  );
}
